import React, { useState } from 'react';
import { Menu, Bell, Search, User, Settings, LogOut, ChevronDown, Wifi, WifiOff, Activity, CheckCircle, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ApiService from '../services/api';
import { useNotifications, NotificationType } from '../context/NotificationContext';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const [searchValue, setSearchValue] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [apiStatus, setApiStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  // Test API connection on component mount
  React.useEffect(() => {
    const testConnection = async () => {
      try {
        await ApiService.checkHealth();
        setApiStatus('connected');
      } catch (error) {
        console.error('API connection failed:', error);
        setApiStatus('disconnected');
      }
    };
    
    testConnection();
    
    // Check connection every 30 seconds
    const interval = setInterval(testConnection, 30000);
    return () => clearInterval(interval);
  }, []);

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-4 h-4 text-success-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-error-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-warning-500" />;
      case 'info': return <Info className="w-4 h-4 text-primary-500" />;
    }
  };

  const getTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} يوم`;
    if (hours > 0) return `${hours} ساعة`;
    if (minutes > 0) return `${minutes} دقيقة`;
    return 'الآن';
  };

  const getApiStatusConfig = () => {
    switch (apiStatus) {
      case 'connected':
        return {
          icon: Wifi,
          text: 'متصل',
          color: 'text-success-600',
          bgColor: 'bg-success-100',
          dotColor: 'bg-success-500'
        };
      case 'disconnected':
        return {
          icon: WifiOff,
          text: 'غير متصل',
          color: 'text-error-600',
          bgColor: 'bg-error-100',
          dotColor: 'bg-error-500'
        };
      default:
        return {
          icon: Activity,
          text: 'جاري الفحص',
          color: 'text-warning-600',
          bgColor: 'bg-warning-100',
          dotColor: 'bg-warning-500'
        };
    }
  };

  const statusConfig = getApiStatusConfig();

  return (
    <header className="header-bg sticky top-0 z-40 border-b border-white/20">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-xl hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
          >
            <Menu className="w-5 h-5 text-gray-700" />
          </button>

          <div className="flex-1 max-w-lg">
            <div className="relative group">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-primary-600 transition-colors duration-200" />
              <input
                type="text"
                placeholder="البحث في المنتجات أو العروض..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="input pr-10 w-full group-focus-within:ring-2 group-focus-within:ring-primary-500/20 group-focus-within:border-primary-400"
              />
              {searchValue && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-full mt-2 w-full bg-white/95 backdrop-blur-md rounded-xl shadow-strong border border-white/50 z-50"
                >
                  <div className="p-4">
                    <p className="text-sm text-gray-600">البحث عن: "{searchValue}"</p>
                    <div className="mt-2 space-y-2">
                      <div className="p-2 hover:bg-primary-50 rounded-lg cursor-pointer transition-colors">
                        <p className="text-sm font-medium">منتجات متطابقة</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* API Status */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${statusConfig.bgColor} ${statusConfig.color}`}>
            <div className={`w-2 h-2 rounded-full ${statusConfig.dotColor} ${apiStatus === 'checking' ? 'animate-pulse' : ''}`}></div>
            <statusConfig.icon className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:block">{statusConfig.text}</span>
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-3 rounded-xl hover:bg-white/20 transition-all duration-200 backdrop-blur-sm group"
            >
              <Bell className="w-5 h-5 text-gray-700 group-hover:text-primary-600 transition-colors" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-error-500 to-error-600 rounded-full flex items-center justify-center animate-pulse">
                  <span className="text-white text-xs font-bold">{unreadCount}</span>
                </span>
              )}
            </button>

            <AnimatePresence>
              {showNotifications && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40"
                    onClick={() => setShowNotifications(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute left-0 mt-2 w-80 bg-white/95 backdrop-blur-md rounded-2xl shadow-strong border border-white/50 z-50 overflow-hidden"
                  >
                    <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-primary-50/50 to-secondary-50/50">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-gray-900">الإشعارات</h3>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{unreadCount} جديد</span>
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllAsRead}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                            >
                              قراءة الكل
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.slice(0, 5).map((notification, index) => (
                          <motion.div 
                            key={notification.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            onClick={() => markAsRead(notification.id)}
                            className={`p-4 border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-secondary-50/30 cursor-pointer transition-all duration-200 ${
                              !notification.read ? 'bg-primary-50/20' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-1">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className={`text-sm font-semibold truncate ${
                                    !notification.read ? 'text-gray-900' : 'text-gray-700'
                                  }`}>
                                    {notification.title}
                                  </h4>
                                  {!notification.read && (
                                    <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                  {notification.message}
                                </p>
                                <span className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                  منذ {getTimeAgo(notification.timestamp)}
                                </span>
                              </div>
                            </div>
                          </motion.div>
                        ))
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>لا توجد إشعارات</p>
                        </div>
                      )}
                    </div>
                    <div className="p-3 bg-gradient-to-r from-gray-50/50 to-white/50 border-t border-gray-200/50">
                      <button className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                        عرض جميع الإشعارات
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/20 transition-all duration-200 backdrop-blur-sm group"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-glow">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-gray-900">المستخدم</p>
                <p className="text-xs text-gray-600">المدير</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {showUserMenu && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute left-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-xl shadow-strong border border-white/50 z-50 overflow-hidden"
                  >
                    <div className="p-2">
                      <button className="dropdown-item w-full text-right flex items-center gap-3 rounded-lg">
                        <User className="w-4 h-4" />
                        الملف الشخصي
                      </button>
                      <button className="dropdown-item w-full text-right flex items-center gap-3 rounded-lg">
                        <Settings className="w-4 h-4" />
                        الإعدادات
                      </button>
                      <hr className="my-2 border-gray-200/50" />
                      <button className="dropdown-item w-full text-right flex items-center gap-3 rounded-lg text-error-600 hover:bg-error-50">
                        <LogOut className="w-4 h-4" />
                        تسجيل الخروج
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;